import { useLayoutEffect, useRef, useState } from "react";
import { Building2, ChevronDown, CircleCheck, Code2, Home, LayoutGrid, List, Lock, Plus, Users, X } from "lucide-react";
import { LogoMark } from "@/components/agent/AgentLayout";
import { PaperPlane } from "@/components/applicant/FlightPath";
import { Field, ListingTag } from "@/components/shared/ui";
import { buttonVariants } from "@/components/ui/button";
import { inputClass } from "@/components/ui/input";
import { gbp } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Caret, FADE_OUT, Scaled, Tap, between, typed } from "@/components/landing/stage";
import { ADDS, ARRIVE, PRESS_MS, PROPERTY } from "@/components/landing/script";

/** The agent's Properties page at a real desktop size, then shrunk to fit (see Scaled). Markup mirrors pages/Properties.js. */
const W = 1120;
const H = 720;
const STREET = `${process.env.PUBLIC_URL}/images/london-street.jpg`;
const MARE = `${process.env.PUBLIC_URL}/images/landing/mare-street.jpg`;

/** Already on the dashboard when the loop starts, oldest last. Street photos are crops of one London terrace. */
const BASE = [
  { id: "lansdowne", address: "12 Lansdowne Crescent, London W11", rent: 2800, applicants: 4, photo: { x: "14%", y: "54%" } },
  { id: "mare", address: PROPERTY.address, rent: PROPERTY.rent, applicants: 2, photo: { src: MARE } },
];
const NAV = [["Applicants", Users], ["Properties", Home], ["Organization", Building2], ["Developers", Code2]];
const TOASTS = [
  ...ADDS.map((a) => ({ at: a.added, until: a.added + 2400, title: "Property added" })),
  { at: ARRIVE, until: ARRIVE + 2800, title: "New application", text: PROPERTY.address, plane: true },
];
const REST = { x: 820, y: 600 };

const pressed = (t, at) => between(t, at, at + PRESS_MS);

/** Centre of `el` in the demo's own (unscaled) coordinates. offsetLeft/Top ignore the shrinking transform. */
const centreIn = (el, root) => {
  let x = el.offsetWidth / 2;
  let y = el.offsetHeight / 2;
  for (let n = el; n && n !== root; n = n.offsetParent) {
    x += n.offsetLeft;
    y += n.offsetTop;
  }
  return { x, y };
};

/** Cards slide to their new place when a property is added at the front (FLIP, in unscaled layout coordinates). */
const useFlip = (keys) => {
  const els = useRef(new Map());
  const last = useRef(new Map());
  useLayoutEffect(() => {
    const now = new Map();
    els.current.forEach((el, key) => {
      const pos = [el.offsetLeft, el.offsetTop];
      now.set(key, pos);
      const was = last.current.get(key);
      if (!was || (was[0] === pos[0] && was[1] === pos[1])) return;
      el.style.transition = "none";
      el.style.transform = `translate(${was[0] - pos[0]}px, ${was[1] - pos[1]}px)`;
      el.getBoundingClientRect();
      el.style.transition = "transform 550ms cubic-bezier(0.2, 0.8, 0.2, 1)";
      el.style.transform = "";
    });
    last.current = now;
  }, [keys]);
  return (key) => (el) => (el ? els.current.set(key, el) : els.current.delete(key));
};

const Photo = ({ photo }) =>
  photo.src ? (
    <img src={photo.src} alt="" className="h-full w-full object-cover" />
  ) : (
    <div className="h-full w-full bg-no-repeat" style={{ backgroundImage: `url(${STREET})`, backgroundSize: "260% auto", backgroundPosition: `${photo.x} ${photo.y}` }} />
  );

const Card = ({ p, fresh, ping, cardRef }) => (
  <div ref={cardRef} className={cn("flex flex-col overflow-hidden rounded-xl border bg-background p-1 transition-shadow duration-500",
    fresh && "animate-in fade-in-0 zoom-in-95 duration-500", ping && "shadow-[0_0_0_2px_hsl(var(--brand-strong))]")}>
    <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-muted">
      <Photo photo={p.photo} />
      <span className="absolute left-2 top-2 rounded-full bg-background shadow-xs"><ListingTag status="available" /></span>
    </div>
    <div className="px-2.5 pb-2 pt-2.5">
      <p className="truncate text-sm font-medium">{p.address}</p>
      <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span><span className="font-medium text-foreground">{gbp(p.rent)}</span> a month</span>
        <span className={cn("inline-flex items-center gap-1 transition-colors", ping && "font-medium text-foreground")}><Users className="size-3" />{p.applicants}</span>
      </div>
    </div>
  </div>
);

const Sidebar = () => (
  <aside className="flex w-60 shrink-0 flex-col gap-2 border-r border-sidebar-border bg-sidebar p-2">
    <div className="flex h-10 items-center gap-2 px-2"><LogoMark /><span className="text-sm font-semibold">Landin</span></div>
    <nav className="flex flex-col gap-0.5 py-3">
      {NAV.map(([label, Icon]) => (
        <span key={label} className={cn("flex h-9 items-center gap-3 rounded-md px-2.5 text-sm", label === "Properties" ? "bg-sidebar-accent font-medium text-foreground" : "text-foreground/75")}>
          <Icon className="size-4" /> {label}
        </span>
      ))}
    </nav>
    <div className="mt-auto border-t border-sidebar-border pt-2">
      <div className="flex items-center gap-2.5 rounded-lg p-1.5">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-foreground text-xs font-medium text-background">SM</span>
        <span className="min-w-0">
          <span className="block text-sm font-medium leading-tight">Sarah Mitchell</span>
          <span className="block truncate text-xs leading-tight text-muted-foreground">sarah@hackneylettings.co.uk</span>
        </span>
      </div>
    </div>
  </aside>
);

const Input = ({ value, placeholder, focused, className, inputRef }) => (
  <div ref={inputRef} className={cn(inputClass, "flex items-center", focused && "border-ring ring-[3px] ring-ring/50", className)}>
    {value ? <span>{value}</span> : <span className="text-muted-foreground">{placeholder}</span>}
    {focused && <Caret />}
  </div>
);

/** The real "Add a property" dialog (AddPropertyDialog + PropertyForm), filled in by the script. */
const AddDialog = ({ t, refs }) => {
  const run = ADDS.filter((a) => t >= a.open).pop();
  const open = !!run && between(t, run.open, run.added);
  const p = run?.property;
  const address = run ? typed(p.address, t, run.address) : "";
  const rent = run && t >= run.rent ? typed(String(p.rent), t, run.rent) : "";
  const date = run && t >= run.date ? typed(p.moveIn, t, run.date) : "";
  const focus = !open ? null : t < run.rent ? "address" : t < run.date ? "rent" : t < run.toSave ? "date" : null;
  const saving = ADDS.some((a) => pressed(t, a.save));
  return (
    <div className={cn("absolute inset-0 z-20 flex items-center justify-center transition-opacity duration-200", open ? "opacity-100" : "opacity-0")}>
      <div className="absolute inset-0 bg-black/40" />
      <div className={cn("relative grid w-full max-w-xl gap-4 rounded-2xl border bg-background p-6 text-sm shadow-lg transition-transform duration-200", open ? "scale-100" : "scale-95")}>
        <div className="flex flex-col gap-1.5 text-left">
          <p className="text-base font-semibold leading-6">Add a property</p>
          <p className="text-sm text-muted-foreground">You can add a photo and fine-tune the criteria after saving.</p>
        </div>
        <X className="absolute right-4 top-4 size-4 opacity-70" />
        <div className="flex flex-col gap-4">
          <Field label="Address" help="Shown to applicants on the apply page.">
            <Input inputRef={(el) => (refs.current.address = el)} value={address} placeholder="Flat 1, 10 Example Road, London N1" focused={focus === "address"} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Monthly rent (£)"><Input value={rent} focused={focus === "rent"} /></Field>
            <Field label="Move-in date"><Input value={date} placeholder="dd/mm/yyyy" focused={focus === "date"} /></Field>
            <Field label="Tenants"><Input value="1" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Minimum income (× rent)" help="Gross yearly income needed, as a multiple of monthly rent."><Input value="30" /></Field>
            <Field label="Guarantor income (× rent)" help="Shown separately. Never changes the applicant's result."><Input value="36" /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <span className={buttonVariants({ variant: "outline", size: "sm" })}>Cancel</span>
            <span ref={(el) => (refs.current.save = el)} className={cn(buttonVariants({ size: "sm" }), "relative overflow-hidden", saving && "scale-[0.97] bg-primary/90")}>
              Add property<Tap on={saving} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Toast = ({ t }) => {
  const active = TOASTS.find((x) => between(t, x.at, x.until));
  const shown = useRef(TOASTS[0]);
  if (active) shown.current = active;
  const toast = shown.current;
  return (
    <div className={cn("absolute left-1/2 top-14 z-40 flex w-[356px] -translate-x-1/2 items-start gap-2 rounded-lg border bg-popover px-4 py-3 text-[13px] shadow-lg transition-all duration-300",
      active ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0")}>
      {toast.plane ? (
        <svg viewBox="-14 -9 28 18" className="mt-0.5 h-3.5 w-6 shrink-0" overflow="visible"><PaperPlane /></svg>
      ) : <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" />}
      <div className="min-w-0">
        <p className="font-medium">{toast.title}</p>
        {toast.text && <p className="truncate text-muted-foreground">{toast.text}</p>}
      </div>
    </div>
  );
};

const Cursor = ({ at, down }) => (
  <svg viewBox="0 0 20 24" className="pointer-events-none absolute left-0 top-0 z-30 h-6 w-5 drop-shadow-sm transition-transform duration-700 ease-in-out"
    style={{ transform: `translate(${at.x - 3}px, ${at.y - 2}px) scale(${down ? 0.85 : 1})` }}>
    <path d="M3 2 L3 18.5 L7.2 14.6 L10.1 21 L12.9 19.8 L10.1 13.5 L15.8 13.5 Z" fill="#111" stroke="#fff" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

const aimAt = (t) => {
  for (const a of ADDS) {
    if (between(t, a.cursor, a.address)) return "add";
    if (between(t, a.address, a.toSave)) return "address";
    if (between(t, a.toSave, a.added + 500)) return "save";
  }
  return "rest";
};

/** Left side of the landing page: a letting agent adding properties, then an application arriving. */
export const AgentDemo = ({ t, className }) => {
  const root = useRef(null);
  const refs = useRef({});
  const [spots, setSpots] = useState({});
  const added = ADDS.filter((a) => t >= a.added).map((a) => ({ ...a.property, applicants: 0, fresh: true })).reverse();
  const cards = [...added, ...BASE];
  const flip = useFlip(cards.map((c) => c.id).join());
  const arrived = t >= ARRIVE;
  const addDown = ADDS.some((a) => pressed(t, a.press));
  const aim = aimAt(t);

  useLayoutEffect(() => {
    const measure = () => setSpots(Object.fromEntries(Object.entries(refs.current).filter(([, el]) => el).map(([k, el]) => [k, centreIn(el, root.current)])));
    measure();
    document.fonts?.ready.then(measure);
  }, []);

  return (
    <div className={cn("overflow-hidden rounded-xl border bg-background shadow-[0_30px_80px_-30px_rgb(0_0_0/0.35)]", className)} data-testid="landing-agent-demo">
      <Scaled width={W} height={H}>
        <div ref={root} className="relative flex h-full flex-col bg-background text-sm text-foreground">
          <div className="flex h-10 shrink-0 items-center gap-2 border-b bg-panel px-4">
            {[0, 1, 2].map((i) => <span key={i} className="size-3 rounded-full bg-muted-foreground/25" />)}
            <span className="mx-auto flex h-6 w-80 items-center justify-center gap-1.5 rounded-md bg-background text-xs text-muted-foreground ring-1 ring-border">
              <Lock className="size-3" /> landin.app/properties
            </span>
            <span className="w-[52px]" />
          </div>
          <div className={cn("flex min-h-0 flex-1 transition-opacity duration-300", t >= FADE_OUT || t < 250 ? "opacity-0" : "opacity-100")}>
            <Sidebar />
            <main className="flex min-w-0 flex-1 flex-col gap-8 px-8 py-8">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-lg font-semibold">Properties</p>
                  <p className="text-sm text-muted-foreground">{cards.length} properties · {cards.length} available</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm shadow-xs">Newest <ChevronDown className="size-3.5 opacity-50" /></span>
                  <span className="flex items-center gap-0.5 rounded-full border p-0.5">
                    <span className="grid size-6 place-items-center rounded-full bg-accent"><LayoutGrid className="size-3.5" /></span>
                    <span className="grid size-6 place-items-center rounded-full"><List className="size-3.5" /></span>
                  </span>
                  <span ref={(el) => (refs.current.add = el)} className={cn(buttonVariants({ size: "sm" }), "relative overflow-hidden", addDown && "scale-[0.97] bg-primary/90")}>
                    <Plus /> Add property<Tap on={addDown} />
                  </span>
                </div>
              </div>
              <div className="relative grid grid-cols-3 gap-3">
                {cards.map((p) => (
                  <Card key={p.id} p={p.id === "mare" && arrived ? { ...p, applicants: p.applicants + 1 } : p} fresh={p.fresh}
                    ping={p.id === "mare" && between(t, ARRIVE, ARRIVE + 2800)} cardRef={flip(p.id)} />
                ))}
              </div>
            </main>
          </div>
          <AddDialog t={t} refs={refs} />
          <Toast t={t} />
          <Cursor at={spots[aim] || REST} down={addDown || ADDS.some((a) => pressed(t, a.save))} />
        </div>
      </Scaled>
    </div>
  );
};
